// Sugalarm — History screen.
//
// Window pills (2h / 6h / 12h / 24h / 7d / 30d, cycle 65 unified set —
// the cycle-9 3h pill is retired) drive a parallel pull of glucose /
// treatment / alert lists *and* the analytics summary; the chart,
// TIR strip, and four stat cards all hang off those four responses.
// No client-side aggregation — the backend already does all the math
// under /api/analytics.
//
// Cycle 65 kiosk-only upgrades:
//   • The chart's fixed 240-px kiosk height is replaced with a
//     measured-fill layout — a ResizeObserver + useLayoutEffect on
//     `.chartWrap` feeds the live container height into HistoryChart
//     so the chart claims whatever space remains between the
//     window-pill row and the TIR + 4 stat cards inside the 1024×600
//     frame.
//   • A chevron expand/collapse button (mirrors the cycle-48 Now-
//     screen `chartExpanded` chevron) flips the chart into a
//     fullscreen state that hides the TIR + stat cards so the chart
//     fills the entire content area.
//   • Pan + pinch-zoom turn on for any window ≥ 6h on the kiosk
//     (`panSliceMs` derived per-window) — long windows (7d / 30d)
//     open showing the most recent 3 days via the shared
//     LONG_WINDOW_DEFAULT_VISIBLE_HOURS constant.
// Web + mobile chart heights and behavior are byte-identical to cycle 64.

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import * as alertsApi from "../../api/alerts";
import * as analyticsApi from "../../api/analytics";
import * as glucoseApi from "../../api/glucose";
import * as treatmentsApi from "../../api/treatments";
import { HistoryChart } from "../../components/glucose/HistoryChart";
import { HistoryLegend } from "../../components/glucose/HistoryLegend";
import { MarkerGlyph } from "../../components/glucose/markerGlyphs";
import { TIRBar } from "../../components/glucose/TIRBar";
import { StatCard } from "../../components/ui/StatCard";
import { Tabs } from "../../components/ui/Tabs";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import { useIsLandscape } from "../../hooks/useIsLandscape";
import { useSettings } from "../../hooks/useSettings";
import { useThresholds } from "../../hooks/useThresholds";
import { displayValue } from "../../lib/glucose";
import { hapticSelect } from "../../lib/haptics";
import type {
  Alert,
  GlucoseSummary,
  Reading,
  Treatment,
} from "../../lib/types";
import styles from "./HistoryScreen.module.css";

// Cycle 65: unified window set — drop the cycle-9 "3h" pill so the
// chart's window vocabulary matches the Now screen exactly.
type WindowId = "2h" | "6h" | "12h" | "24h" | "7d" | "30d";

const WINDOWS: { id: WindowId; label: string; hours: number }[] = [
  { id: "2h", label: "2h", hours: 2 },
  { id: "6h", label: "6h", hours: 6 },
  { id: "12h", label: "12h", hours: 12 },
  { id: "24h", label: "24h", hours: 24 },
  { id: "7d", label: "7d", hours: 24 * 7 },
  { id: "30d", label: "30d", hours: 24 * 30 },
];

// Cycle 65: keep this in sync with the same-named constant in
// NowScreen — both surfaces open 7d/30d on the kiosk centered on the
// most recent 3 days, then pinch/pan reveals older data.
const LONG_WINDOW_DEFAULT_VISIBLE_HOURS = 72;
const isLongWindow = (h: number): boolean => h === 168 || h === 720;
// Windows that pan a fixed slice. 0.65.2: the 2h pill now also pans
// — 1h slice on every variant, so dots render full-size and the
// hour is swipeable. Other pannable windows (6 / 12 / 24 / 7d / 30d)
// stay kiosk-only on History per cycle 64. Shared with the Now
// screen via convention, not import.
const PANNABLE_HOURS = new Set<number>([2, 6, 12, 24, 168, 720]);

interface Props {
  variant: FrameVariant;
}

export function HistoryScreen({ variant }: Props) {
  const { units, settings } = useSettings();
  const h24 = settings?.display_24h ?? false;
  const thresholds = useThresholds();
  // Cycle 115: mobile landscape gets a no-scroll layout — one-row header,
  // measured-fill chart, and a four-chip stat strip.
  const isLandscape = useIsLandscape();
  const mobileLandscape = variant === "mobile" && isLandscape;
  const [windowId, setWindowId] = useState<WindowId>("24h");
  const [readings, setReadings] = useState<Reading[]>([]);
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [summary, setSummary] = useState<GlucoseSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [endMs, setEndMs] = useState<number>(() => Date.now());
  const [legendOpen, setLegendOpen] = useState(false);
  const infoBtnRef = useRef<HTMLButtonElement | null>(null);
  // Cycle 65 (kiosk only): chevron-driven fullscreen state. When true
  // the chart fills the content area over the TIR + stat cards.
  const [kioskFullscreen, setKioskFullscreen] = useState(false);
  // Cycle 65 (kiosk only): measured chart height for the fill layout.
  // 240 is the cycle-9 fixed kiosk height — kept as a sane initial
  // value before the first layout measurement lands.
  const chartWrapRef = useRef<HTMLDivElement | null>(null);
  const [kioskChartH, setKioskChartH] = useState<number>(240);

  const hours = WINDOWS.find((w) => w.id === windowId)?.hours ?? 24;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const end = Date.now();
    setEndMs(end);

    (async () => {
      const [g, t, a, s] = await Promise.all([
        glucoseApi.history(hours, 30_000),
        treatmentsApi.list(hours, 1_000),
        alertsApi.list(hours, 500),
        analyticsApi.summary(hours),
      ]);
      if (cancelled) return;

      const errs: string[] = [];
      if (g.ok) {
        const asc = [...g.data.readings].sort(
          (x, y) =>
            new Date(x.timestamp).getTime() - new Date(y.timestamp).getTime(),
        );
        setReadings(asc);
      } else errs.push(`readings: ${g.error.message}`);
      if (t.ok) setTreatments(t.data.treatments);
      else errs.push(`treatments: ${t.error.message}`);
      if (a.ok) setAlerts(a.data.alerts);
      else errs.push(`alerts: ${a.error.message}`);
      if (s.ok) setSummary(s.data);
      else errs.push(`summary: ${s.error.message}`);

      setError(errs.length ? errs.join(" · ") : null);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [hours]);

  const startMs = endMs - hours * 60 * 60 * 1000;

  // Cycle 65: kiosk-only measured chart height. The chartWrap claims
  // `flex: 1` of the column — observe its live size and feed it into
  // HistoryChart. Same useLayoutEffect + ResizeObserver pattern as the
  // Now screen's chart-fill.
  // Cycle 115: the measured-fill chart now also runs for mobile landscape
  // (so the History chart fills the middle region between the one-row
  // header and the four-chip stat strip with zero scrolling).
  useLayoutEffect(() => {
    if (variant !== "kiosk" && !mobileLandscape) return;
    const el = chartWrapRef.current;
    if (!el) return;
    const apply = () => {
      const h = Math.floor(el.clientHeight);
      if (h > 0) setKioskChartH(h);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [variant, kioskFullscreen, mobileLandscape]);

  // Cycle 65 + 0.65.2 + 0.65.3: pan slice computation. 2h pans on
  // every variant (1h slice). Other pannable windows pan on **kiosk
  // and mobile** (0.65.3 — mobile History asked for a zoomed +
  // smooth-scrollable view on every window); width vocabulary
  // mirrors the Now screen (mobile 1h / kiosk 3h for short windows,
  // shared 3-day default for long windows). Web History keeps cycle
  // 64's un-zoomed full-window view on non-2h pannable windows.
  const panSliceMs = useMemo(() => {
    if (!PANNABLE_HOURS.has(hours)) return undefined;
    if (hours === 2) {
      // Universal 2h → 1h slice on kiosk + web + mobile.
      return 1 * 60 * 60 * 1000;
    }
    if (variant === "mobile") {
      const sliceH = isLongWindow(hours)
        ? LONG_WINDOW_DEFAULT_VISIBLE_HOURS
        : 1;
      return sliceH * 60 * 60 * 1000;
    }
    // Cycle 183 (kiosk + web): open zoomed into HALF the selected window
    // (6→3h / 12→6h / 24→12h) to match the Now chart; long windows keep
    // the 72h (3-day) default. Was: kiosk fixed 3h, web un-zoomed
    // full-window (cycle 64) — web History is now pannable here too.
    const sliceH = isLongWindow(hours)
      ? LONG_WINDOW_DEFAULT_VISIBLE_HOURS
      : hours / 2;
    return sliceH * 60 * 60 * 1000;
  }, [variant, hours]);

  // Cycle 183: inertial flick on the kiosk + web History charts only.
  const chartFlick = variant === "kiosk" || variant === "web";

  // Cycle 183: low alarm EPISODES in the selected window — rows with
  // level ∈ {low, urgent_low} from the alerts the chart already loaded
  // (same definition the cycle-170 Dashboard uses). Client-side only.
  const lows = useMemo(() => {
    let total = 0;
    let urgent = 0;
    for (const a of alerts) {
      if (a.level === "low") total++;
      else if (a.level === "urgent_low") {
        total++;
        urgent++;
      }
    }
    return { total, urgent };
  }, [alerts]);

  const stats = useMemo(() => {
    const fmt = (n: number | null, digits = 0): string =>
      n == null ? "—" : digits > 0 ? n.toFixed(digits) : Math.round(n).toString();
    return {
      avg:
        summary?.avg_sgv == null
          ? "—"
          : displayValue(Math.round(summary.avg_sgv), units),
      gmi: fmt(summary?.gmi_pct ?? null, 1),
      cv: fmt(summary?.cv_pct ?? null, 0),
      count: summary?.reading_count ?? readings.length,
    };
  }, [summary, readings.length, units]);

  // Cycle 45: mobile + kiosk use the "Legend" pill + centered modal
  // popover; desktop keeps the cycle-44 round `i` + anchored popover.
  const centeredLegend = variant === "mobile" || variant === "kiosk";

  // Cycle 115: four compact landscape stat chips, all from the already-
  // fetched summary (no new math / fetch): TIR%, Average, Lowest, Highest.
  const landscapeChips: { label: string; value: string; unit?: string }[] = [
    {
      label: "Time in range",
      value:
        summary?.tir?.in_range_pct == null
          ? "—"
          : `${Math.round(summary.tir.in_range_pct)}`,
      unit: "%",
    },
    { label: "Average", value: stats.avg, unit: units },
    {
      label: "Lowest",
      value:
        summary?.min_sgv == null
          ? "—"
          : displayValue(summary.min_sgv, units),
    },
    {
      label: "Highest",
      value:
        summary?.max_sgv == null
          ? "—"
          : displayValue(summary.max_sgv, units),
    },
  ];

  return (
    <div
      className={`${styles.root} ${styles[variant] ?? ""} ${
        variant === "kiosk" && kioskFullscreen ? styles.kioskFullscreen : ""
      } ${mobileLandscape ? styles.landscape : ""}`}
    >
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h2 className={styles.title}>History</h2>
          <span className={styles.titleSub}>
            {readings.length} readings · {treatments.length} events · {alerts.length} alerts
          </span>
          {centeredLegend ? (
            <button
              ref={infoBtnRef}
              type="button"
              className={`${styles.legendBtn} ${
                legendOpen ? styles.legendBtnActive : ""
              }`}
              aria-label="Show chart legend"
              aria-haspopup="dialog"
              aria-expanded={legendOpen}
              onClick={() => setLegendOpen((v) => !v)}
            >
              <MarkerGlyph
                kind="reading"
                color="var(--zone-ok)"
                wrap
                size={12}
              />
              Legend
            </button>
          ) : (
            <button
              ref={infoBtnRef}
              type="button"
              className={`${styles.infoBtn} ${
                legendOpen ? styles.infoBtnActive : ""
              }`}
              aria-label="Show chart legend"
              aria-haspopup="dialog"
              aria-expanded={legendOpen}
              onClick={() => setLegendOpen((v) => !v)}
            >
              i
            </button>
          )}
        </div>
        <Tabs<WindowId>
          tabs={WINDOWS.map((w) => ({ id: w.id, label: w.label }))}
          activeId={windowId}
          onChange={(id) => {
            if (variant === "mobile") hapticSelect();
            setWindowId(id);
          }}
          className={styles.windowTabs}
          accentActive={chartFlick}
        />
      </div>
      {legendOpen && (
        <HistoryLegend
          anchorEl={infoBtnRef.current}
          mode={centeredLegend ? "centered" : "anchored"}
          onClose={() => setLegendOpen(false)}
        />
      )}

      <div ref={chartWrapRef} className={styles.chartWrap}>
        {/* Cycle 65 kiosk-only: chevron in the top-right corner of the
            chart wrap flips fullscreen mode. Mirrors the Now-screen
            cycle-48 chartExpanded chevron — same glyph, same a11y. */}
        {variant === "kiosk" && (
          <button
            type="button"
            className={styles.kioskFsBtn}
            aria-label={
              kioskFullscreen ? "Collapse chart" : "Expand chart fullscreen"
            }
            aria-expanded={kioskFullscreen}
            onClick={() => setKioskFullscreen((v) => !v)}
          >
            {kioskFullscreen ? "▼" : "▲"}
          </button>
        )}
        {loading && readings.length === 0 ? (
          <div className={styles.loading}>Loading…</div>
        ) : (
          <HistoryChart
            readings={readings}
            treatments={treatments}
            alerts={alerts}
            thresholds={thresholds}
            rangeStartMs={startMs}
            rangeEndMs={endMs}
            // Cycle 65: kiosk → measured fill; mobile + web unchanged.
            height={
              variant === "kiosk" || mobileLandscape
                ? Math.max(160, kioskChartH)
                : variant === "mobile"
                  ? 220
                  : 320
            }
            units={units}
            // Cycle 65 + 0.65.2: kiosk gets pan + pinch on every
            // pannable window. Web + mobile History gets pan + pinch
            // on the 2h pill only (per 0.65.2 user spec). Pinch is
            // turned on whenever a slice is active.
            panSliceMs={panSliceMs}
            pinchZoom={panSliceMs != null}
            // Cycle 186.2: desktop smooth zoom — mouse wheel / trackpad pinch
            // zooms the slice (web only; touch surfaces keep two-finger pinch).
            wheelZoom={variant === "web"}
            flickMomentum={chartFlick}
            // Cycle 65.1: dots / treatment glyphs / alert rings are
            // tappable on the History tab — same lightweight tooltip
            // pattern the Now screen has used since cycle 57.
            showTooltips
            h24={h24}
            // Cycle 208: green partition pill confirming the active window
            // (the WindowId is the user-facing label: 2h…30d).
            partitionLabel={windowId}
          />
        )}
      </div>

      {mobileLandscape ? (
        // Cycle 115: four compact chips pinned beneath the chart so the
        // whole screen fits with zero scrolling in landscape.
        <div className={styles.statChips}>
          {landscapeChips.map((c) => (
            // Cycle 169: landscape chips are mobile-only → always glass.
            <div key={c.label} className={`${styles.statChip} ${styles.glass}`}>
              <span className={styles.statChipLabel}>{c.label}</span>
              <span className={styles.statChipValue}>
                {c.value}
                {c.unit ? (
                  <span className={styles.statChipUnit}>{c.unit}</span>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.statsRow}>
          {/* Cycle 169: frosted glass on the MOBILE frame (its --glass-*
              come from :root/.dark). Cycle 183: kiosk + web now glass too
              via the History-scoped `.kiosk/.web .statsRow` --glass-*
              override below; mobile keeps its own tokens → byte-identical.
              Foreground numerals stay fully opaque. */}
          <div className={`${styles.tirCard} ${styles.glass}`}>
            <span className={styles.tirCardLabel}>Time in range</span>
            <TIRBar
              tir={
                summary?.tir ?? {
                  urgent_low_pct: 0,
                  low_pct: 0,
                  in_range_pct: 0,
                  high_pct: 0,
                  urgent_high_pct: 0,
                }
              }
            />
          </div>
          <StatCard label="Avg" value={stats.avg} unit={units} glass />
          <StatCard label="GMI" value={stats.gmi} unit="%" glass />
          <StatCard label="CV" value={stats.cv} unit="%" glass />
          <StatCard label="Readings" value={stats.count} glass />
          {/* Cycle 183: Lows = low + urgent_low episodes in the window
              (client-side). kiosk + web only, so the mobile stat row stays
              byte-identical (acceptance check #6). */}
          {chartFlick && (
            <StatCard
              label="Lows"
              value={lows.total}
              sub={lows.urgent > 0 ? `${lows.urgent} urgent` : undefined}
              glass
            />
          )}
        </div>
      )}

      {error && (
        <div style={{ color: "var(--zone-urgent)", fontSize: "var(--type-floor)" }}>{error}</div>
      )}
    </div>
  );
}
